import React, { useState } from 'react';
import { Label } from './ui/label';
import { Input } from './ui/input';
import { Textarea } from './ui/textarea';
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from './ui/select';
import { Slider } from './ui/slider';
import { Checkbox } from './ui/checkbox';
import { Button } from './ui/button';
import {
  Collapsible,
  CollapsibleContent,
  CollapsibleTrigger,
} from './ui/collapsible';
import VoiceInput from './VoiceInput';
import { tareasInfo } from '../lib/questions';
import { ChevronDown, ChevronUp, Info } from 'lucide-react';

export function QuestionInput({ question, value, onChange, disabled, showError }) {
  const [isInfoOpen, setIsInfoOpen] = useState(false);
  const [otherText, setOtherText] = useState(() => {
    // If value contains "Otro:" extract the custom text
    if (Array.isArray(value)) {
      const otherValue = value.find(v => v && v.startsWith('Otro:'));
      return otherValue ? otherValue.replace('Otro:', '').trim() : '';
    }
    return '';
  });

  const handleChange = (newValue) => {
    onChange(question.id, newValue);
  };

  const handleVoiceTranscript = (transcript) => {
    const currentValue = value || '';
    const newValue = currentValue ? `${currentValue} ${transcript}` : transcript;
    handleChange(newValue);
  };

  const handleMultiselectChange = (option, checked) => {
    const currentValues = value ? (Array.isArray(value) ? value : [value]) : [];
    let newValues;
    
    if (checked) {
      // Si selecciona "Ninguna", deseleccionar todo lo demás
      if (option === 'Ninguna') {
        newValues = ['Ninguna'];
        setOtherText('');
      } else if (option === 'Otro') {
        // Add "Otro" placeholder, user will fill in the text
        newValues = [...currentValues.filter(v => v !== 'Ninguna' && !v?.startsWith('Otro')), 'Otro'];
      } else {
        // Si selecciona otra cosa, quitar "Ninguna"
        newValues = [...currentValues.filter(v => v !== 'Ninguna'), option];
      }
    } else {
      if (option === 'Otro') {
        newValues = currentValues.filter(v => v !== 'Otro' && !v?.startsWith('Otro:'));
        setOtherText('');
      } else {
        newValues = currentValues.filter(v => v !== option);
      }
    }
    
    handleChange(newValues);
  };

  const handleOtherTextChange = (text) => {
    setOtherText(text);
    const currentValues = value ? (Array.isArray(value) ? value : [value]) : [];
    // Remove any existing "Otro" or "Otro:..." entry and add the new one
    const filteredValues = currentValues.filter(v => v !== 'Otro' && !v?.startsWith('Otro:'));
    if (text.trim()) {
      handleChange([...filteredValues, `Otro: ${text.trim()}`]);
    } else {
      handleChange([...filteredValues, 'Otro']);
    }
  };

  const isOtroSelected = () => {
    if (!Array.isArray(value)) return false;
    return value.some(v => v === 'Otro' || v?.startsWith('Otro:'));
  };

  const isEmpty = () => {
    if (value === undefined || value === null || value === '') return true;
    if (Array.isArray(value) && value.length === 0) return true;
    if (Array.isArray(value) && value.every(v => !v)) return true;
    return false;
  };

  const hasError = showError && isEmpty();

  // Get info content if question has expandable info
  const getInfoContent = () => {
    if (question.hasInfo && question.infoKey === 'tareasInfo') {
      return tareasInfo;
    }
    return null;
  };

  const infoContent = getInfoContent();

  const renderInput = () => {
    switch (question.type) {
      case 'text':
        return (
          <div className="flex gap-2">
            <Input
              id={question.id}
              value={value || ''}
              onChange={(e) => handleChange(e.target.value)}
              placeholder="Tu respuesta..."
              disabled={disabled}
              className={hasError ? 'border-red-500' : ''}
              data-testid={`question-input-${question.id}`}
            />
            <VoiceInput onTranscript={handleVoiceTranscript} disabled={disabled} />
          </div>
        );

      case 'textarea':
        return (
          <div className="space-y-2">
            <div className="flex gap-2 items-start">
              <Textarea
                id={question.id}
                value={value || ''}
                onChange={(e) => handleChange(e.target.value)}
                placeholder="Escribí tu respuesta aquí..."
                rows={3}
                disabled={disabled}
                className={`flex-1 ${hasError ? 'border-red-500' : ''}`}
                data-testid={`question-textarea-${question.id}`}
              />
              <VoiceInput onTranscript={handleVoiceTranscript} disabled={disabled} />
            </div>
          </div>
        );

      case 'select':
        return (
          <Select
            value={value || ''}
            onValueChange={(newVal) => {
              try {
                handleChange(newVal);
              } catch (err) {
                console.error('Error handling select change for', question.id, err);
              }
            }}
            disabled={disabled}
          >
            <SelectTrigger 
              className={hasError ? 'border-red-500' : ''}
              data-testid={`question-select-${question.id}`}
            >
              <SelectValue placeholder="Seleccioná una opción" />
            </SelectTrigger>
            <SelectContent>
              {question.options?.map((option) => (
                <SelectItem key={option} value={option}>
                  {option}
                </SelectItem>
              ))}
            </SelectContent>
          </Select>
        );

      case 'multiselect':
        const selectedValues = value ? (Array.isArray(value) ? value : [value]) : [];
        const hasOtroSelected = isOtroSelected();
        return (
          <div className={`space-y-3 p-4 rounded-lg ${hasError ? 'bg-red-50 border border-red-200' : 'bg-muted/30'}`}>
            <p className="text-sm text-muted-foreground mb-2">Podés seleccionar más de una opción:</p>
            {question.options?.map((option) => {
              const isChecked = option === 'Otro' 
                ? hasOtroSelected 
                : selectedValues.includes(option);
              return (
                <div key={option} className="flex items-center space-x-3">
                  <Checkbox
                    id={`${question.id}-${option}`}
                    checked={isChecked}
                    onCheckedChange={(checked) => handleMultiselectChange(option, checked)}
                    disabled={disabled}
                    data-testid={`question-multiselect-${question.id}-${option}`}
                  />
                  <Label 
                    htmlFor={`${question.id}-${option}`}
                    className="text-sm font-normal cursor-pointer"
                  >
                    {option}
                  </Label>
                </div>
              );
            })}
            {/* Campo de texto para "Otro" si allowOther está habilitado y "Otro" está seleccionado */}
            {question.allowOther && hasOtroSelected && (
              <div className="mt-2 ml-6">
                <Input
                  value={otherText}
                  onChange={(e) => handleOtherTextChange(e.target.value)}
                  placeholder="Especificá cuál..."
                  disabled={disabled}
                  className="max-w-md"
                  data-testid={`question-other-text-${question.id}`}
                />
              </div>
            )}
          </div>
        );

      case 'scale':
        return (
          <div className="space-y-3">
            <Slider
              value={[value || 5]}
              onValueChange={([val]) => handleChange(val)}
              min={1}
              max={10}
              step={1}
              disabled={disabled}
              data-testid={`question-scale-${question.id}`}
            />
            <div className="flex justify-between text-xs text-muted-foreground">
              <span>1 - Muy en desacuerdo</span>
              <span className="font-semibold text-primary text-lg">{value || 5}</span>
              <span>10 - Muy de acuerdo</span>
            </div>
          </div>
        );

      default:
        return (
          <div className="flex gap-2">
            <Input
              id={question.id}
              value={value || ''}
              onChange={(e) => handleChange(e.target.value)}
              placeholder="Tu respuesta..."
              disabled={disabled}
              className={hasError ? 'border-red-500' : ''}
              data-testid={`question-input-${question.id}`}
            />
            <VoiceInput onTranscript={handleVoiceTranscript} disabled={disabled} />
          </div>
        );
    }
  };

  return (
    <div className="space-y-2">
      <Label htmlFor={question.id} className="text-base font-medium flex items-start gap-1">
        {question.text}
        <span className="text-red-500">*</span>
      </Label>
      
      {/* Expandable info section */}
      {infoContent && (
        <Collapsible open={isInfoOpen} onOpenChange={setIsInfoOpen}>
          <CollapsibleTrigger asChild>
            <Button 
              variant="ghost" 
              size="sm" 
              className="h-auto py-1 px-2 text-xs text-primary hover:text-primary/80"
            >
              <Info className="w-3 h-3 mr-1" />
              {infoContent.title}
              {isInfoOpen ? <ChevronUp className="w-3 h-3 ml-1" /> : <ChevronDown className="w-3 h-3 ml-1" />}
            </Button>
          </CollapsibleTrigger>
          <CollapsibleContent className="mt-2">
            <div className="p-3 bg-blue-50 border border-blue-200 rounded-lg text-sm">
              <div className="whitespace-pre-line text-muted-foreground">
                {infoContent.content.split('**').map((part, i) => 
                  i % 2 === 1 ? <strong key={i} className="text-foreground">{part}</strong> : part
                )}
              </div>
            </div>
          </CollapsibleContent>
        </Collapsible>
      )}
      
      {question.note && (
        <p className="text-xs text-amber-600 italic">{question.note}</p>
      )}
      {renderInput()}
      {hasError && (
        <p className="text-xs text-red-500">Esta pregunta es obligatoria</p>
      )}
    </div>
  );
}

export default QuestionInput;
